import { useQueryClient } from "@tanstack/react-query";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { api, configureClient } from "./api/client";
import { isApiError } from "./api/errors";
import { clearAllDrafts } from "./draftStorage";
import { getToken, setToken } from "./session";
import type { User } from "./types";

/** Warn this long before the fixed 12-hour session ends (there is no refresh). */
export const EXPIRY_WARNING_MS = 10 * 60 * 1000;

export type Credentials = { email: string; password: string; totp?: string };

type AuthContext = {
  status: "loading" | "anonymous" | "authenticated";
  user: User | null;
  roles: string[];
  isAdmin: boolean;
  /** Wall-clock time the session ends, computed from the server's `seconds_left` so clock skew
   * between this computer and the server does not matter. */
  expiresAt: number | null;
  /** A request came back 401 (or the session ran out) while signed in: show the sign-in dialog. */
  expired: boolean;
  /** Ten minutes or less remain. */
  expiringSoon: boolean;
  signIn: (c: Credentials) => Promise<void>;
  /** Sign in again from the dialog. Must be the same person, so unsaved work stays theirs. */
  resume: (c: Credentials) => Promise<void>;
  signOut: () => Promise<void>;
  openSessionDialog: () => void;
};

const Ctx = createContext<AuthContext | null>(null);

async function loadIdentity(): Promise<{ user: User; expiresAt: number }> {
  const [me, session] = await Promise.all([api.GET("/users/me"), api.GET("/auth/session")]);
  return { user: me.data!, expiresAt: Date.now() + session.data!.seconds_left * 1000 };
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<AuthContext["status"]>(getToken() ? "loading" : "anonymous");
  const [user, setUser] = useState<User | null>(null);
  const [expiresAt, setExpiresAt] = useState<number | null>(null);
  const [expired, setExpired] = useState(false);
  const [expiringSoon, setExpiringSoon] = useState(false);
  const statusRef = useRef(status);
  useEffect(() => {
    statusRef.current = status;
  }, [status]);

  const clear = useCallback(() => {
    setToken(null);
    setUser(null);
    setExpiresAt(null);
    setExpired(false);
    setExpiringSoon(false);
    setStatus("anonymous");
    queryClient.clear();
    clearAllDrafts();
  }, [queryClient]);

  // A 401 anywhere while signed in opens the dialog instead of throwing the person away. During
  // the first load (a stale token from an earlier visit) it just means "not signed in".
  useEffect(() => {
    configureClient({
      getToken,
      onUnauthorized: () => {
        if (statusRef.current === "authenticated") setExpired(true);
      },
    });
  }, []);

  useEffect(() => {
    if (!getToken()) return;
    let cancelled = false;
    loadIdentity()
      .then(({ user, expiresAt }) => {
        if (cancelled) return;
        setUser(user);
        setExpiresAt(expiresAt);
        setStatus("authenticated");
      })
      .catch(() => {
        if (!cancelled) clear();
      });
    return () => {
      cancelled = true;
    };
  }, [clear]);

  // Warn before the session ends, and open the dialog when it does.
  useEffect(() => {
    if (status !== "authenticated" || expiresAt === null) return;
    const tick = () => {
      const left = expiresAt - Date.now();
      setExpiringSoon(left <= EXPIRY_WARNING_MS && left > 0);
      if (left <= 0) setExpired(true);
    };
    tick();
    const id = window.setInterval(tick, 15_000);
    return () => window.clearInterval(id);
  }, [status, expiresAt]);

  const establish = useCallback(async (c: Credentials) => {
    const body: { email: string; password: string; totp?: string } = {
      email: c.email.trim(),
      password: c.password,
    };
    if (c.totp?.trim()) body.totp = c.totp.trim();
    const { data } = await api.POST("/auth/login", { body });
    setToken(data!.token);
    try {
      return await loadIdentity();
    } catch (e) {
      setToken(null);
      throw e;
    }
  }, []);

  const signIn = useCallback(
    async (c: Credentials) => {
      const identity = await establish(c);
      setUser(identity.user);
      setExpiresAt(identity.expiresAt);
      setExpired(false);
      setExpiringSoon(false);
      setStatus("authenticated");
    },
    [establish],
  );

  const resume = useCallback(
    async (c: Credentials) => {
      const identity = await establish(c);
      if (user && identity.user.id !== user.id) {
        // Someone else signed in on top of an unsaved session: refuse, and end that session.
        await api.POST("/auth/logout").catch(() => {});
        setToken(null);
        throw Object.assign(new Error("different user"), { code: "different_user" });
      }
      setUser(identity.user);
      setExpiresAt(identity.expiresAt);
      setExpired(false);
      setExpiringSoon(false);
      // Whatever failed with a 401 can now succeed: refetch it. Screens and drafts stay mounted.
      await queryClient.invalidateQueries();
    },
    [establish, queryClient, user],
  );

  const signOut = useCallback(async () => {
    try {
      await api.POST("/auth/logout");
    } catch (e) {
      if (!isApiError(e)) throw e; // a 401 here just means it was already over
    }
    clear();
  }, [clear]);

  const value = useMemo<AuthContext>(
    () => ({
      status,
      user,
      roles: user?.roles ?? [],
      isAdmin: !!user?.roles.includes("reviewer_admin"),
      expiresAt,
      expired,
      expiringSoon,
      signIn,
      resume,
      signOut,
      openSessionDialog: () => setExpired(true),
    }),
    [status, user, expiresAt, expired, expiringSoon, signIn, resume, signOut],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

/** Training mode: screens below see the practice user and roles, while the real session (token,
 * expiry, the sign-in dialog) carries on underneath untouched. */
export function AuthOverride({
  user,
  signOut,
  children,
}: {
  user: User;
  signOut: () => Promise<void>;
  children: React.ReactNode;
}) {
  const real = useAuth();
  const value = useMemo<AuthContext>(
    () => ({
      ...real,
      user,
      roles: user.roles,
      isAdmin: user.roles.includes("reviewer_admin"),
      signOut,
    }),
    [real, user, signOut],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthContext {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
